<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  checkFileSizeLimit,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import Spinner from 'shared/components/Spinner.vue';
import { MAX_CANNED_ATTACHMENTS } from 'dashboard/constants/cannedResponse';

const ACCEPTED = 'image/*,video/*,audio/*,application/pdf';

export default {
  name: 'CannedAttachmentInput',
  components: { Spinner },
  props: {
    // quantos anexos o pai já tem (salvos + novos), para não passar do limite
    currentCount: { type: Number, default: 0 },
  },
  emits: ['uploaded'],
  data() {
    return { uploadState: 'idle', accept: ACCEPTED };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    maxSizeMB() {
      return resolveMaximumFileUploadSize(
        this.globalConfig.maximumFileUploadSize
      );
    },
    label() {
      const map = {
        idle: 'CANNED_MGMT.ATTACHMENTS.LABEL_IDLE',
        processing: 'CANNED_MGMT.ATTACHMENTS.LABEL_UPLOADING',
        uploaded: 'CANNED_MGMT.ATTACHMENTS.LABEL_UPLOADED',
        failed: 'CANNED_MGMT.ATTACHMENTS.LABEL_UPLOAD_FAILED',
      };
      return this.$t(map[this.uploadState]);
    },
  },
  methods: {
    async onChangeFile(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;

      // corta o excedente ANTES de subir: o backend rejeitaria o save com 422 e
      // cada arquivo já enviado ficaria como blob órfão no storage
      const remaining = MAX_CANNED_ATTACHMENTS - this.currentCount;
      let accepted = files;
      if (files.length > remaining) {
        useAlert(
          this.$t('CANNED_MGMT.ATTACHMENTS.COUNT_LIMIT', {
            max: MAX_CANNED_ATTACHMENTS,
          })
        );
        accepted = remaining > 0 ? files.slice(0, remaining) : [];
      }
      if (!accepted.length) {
        // o finally abaixo não roda neste caminho; sem limpar, reselecionar o
        // mesmo arquivo não dispararia change
        event.target.value = '';
        return;
      }

      this.uploadState = 'processing';
      let uploaded = 0;
      try {
        for (const file of accepted) {
          if (!checkFileSizeLimit({ file }, this.maxSizeMB)) {
            useAlert(
              this.$t('CANNED_MGMT.ATTACHMENTS.SIZE_LIMIT', {
                fileName: file.name,
                size: this.maxSizeMB,
              })
            );
            // eslint-disable-next-line no-continue
            continue;
          }
          // eslint-disable-next-line no-await-in-loop
          const { blobId, fileUrl } = await this.$store.dispatch(
            'uploadCannedAttachment',
            file
          );
          // shape exigido pelo AttachmentsPreview (ele faz type.includes sem guard)
          this.$emit('uploaded', {
            id: blobId,
            blobId,
            isNew: true,
            thumb: fileUrl,
            resource: {
              name: file.name,
              type: file.type,
              size: file.size,
            },
          });
          uploaded += 1;
        }
        // não anunciar "Enviado" se todo o lote foi barrado pelo limite de tamanho
        this.uploadState = uploaded > 0 ? 'uploaded' : 'idle';
      } catch (error) {
        this.uploadState = 'failed';
        useAlert(this.$t('CANNED_MGMT.ATTACHMENTS.UPLOAD_ERROR'));
      } finally {
        event.target.value = '';
      }
    },
  },
};
</script>

<template>
  <label class="input-wrapper" :class="uploadState">
    <input
      v-if="uploadState !== 'processing'"
      type="file"
      name="attachment"
      multiple
      :accept="accept"
      @change="onChangeFile"
    />
    <Spinner v-if="uploadState === 'processing'" />
    <fluent-icon v-if="uploadState === 'idle'" icon="file-upload" />
    <fluent-icon
      v-if="uploadState === 'uploaded'"
      icon="checkmark-circle"
      type="outline"
      class="success-icon"
    />
    <fluent-icon
      v-if="uploadState === 'failed'"
      icon="dismiss-circle"
      type="outline"
      class="error-icon"
    />
    <p class="file-button">{{ label }}</p>
  </label>
</template>

<style scoped>
input[type='file'] {
  @apply hidden;
}
.input-wrapper {
  @apply flex h-8 bg-n-background py-1 px-2 items-center text-xs cursor-pointer rounded-lg border border-dashed border-n-strong gap-2;
}
.success-icon {
  @apply text-n-teal-9;
}
.error-icon {
  @apply text-n-ruby-9;
}
.processing {
  @apply cursor-not-allowed opacity-90;
}
.file-button {
  @apply whitespace-nowrap overflow-hidden text-ellipsis w-full mb-0;
}
</style>
